import React, { KeyboardEvent, MouseEventHandler } from "react";

import { TextProvider } from "../../../context/Text";
import { TypographyProvider, useTypography } from "../../../context/Typography";
import { useContentColor } from "../../../styles/colors/content/useContentColor";
import { ContentEmphasis, KeyColorRole } from "../../../styles/colors/types";
import {
  typographyBodyLineHeightMap,
  TypographyBodySize,
  typographyBodySizeMap,
  TypographyLineHeight
} from "../../../styles/typography/typography";
import { Box } from "../../layout";
import getLinkStyles from "../utils/getLinkStyles";

type Props = {
  children: React.ReactNode | string;
  onClick: MouseEventHandler;
  disabled?: boolean;
  size?: TypographyBodySize;
  lineHeight?: TypographyLineHeight;
  tone?: KeyColorRole;
  emphasis?: ContentEmphasis;
  onHighEmphasisSurface?: boolean;
  disableInteractiveStyles?: boolean;
};

export default function TextButton({
  children,
  onClick,
  disabled = false,
  size: sizeProp,
  lineHeight: lineHeightProp,
  tone = "primary",
  emphasis = "high",
  onHighEmphasisSurface = false,
  disableInteractiveStyles = false
}: Props) {
  const buttonRef = React.useRef<HTMLElement>();

  const color = useContentColor({ tone, emphasis, onHighEmphasisSurface });

  const styleProps = getLinkStyles(disabled, disableInteractiveStyles);

  const {
    typographyBodySize: typographyBodySizeFromContext,
    lineHeight: lineHeightFromContext
  } = useTypography();

  const size = sizeProp || typographyBodySizeFromContext || "medium";
  const lineHeight = lineHeightProp || lineHeightFromContext || "small";

  const onKeyDown = React.useCallback((event: KeyboardEvent) => {
    if (buttonRef.current && (event.key === "Enter" || event.key === " ")) {
      event.preventDefault();
      buttonRef.current.click();
    }
  }, []);

  return (
    <Box
      component="span"
      UNSAFE_role="button"
      tabindex={0}
      onClick={e => {
        !disabled && onClick(e);
      }}
      onKeyDown={onKeyDown}
      ref={buttonRef}
      {...typographyBodySizeMap[size]}
      {...typographyBodyLineHeightMap[lineHeight]}
      color={color}
      backgroundColor="transparent"
      borderColor="transparent"
      cursor="pointer"
      fontWeight="bold"
      {...styleProps}
    >
      <TextProvider value>
        <TypographyProvider value={{ typographyBodySize: size }}>
          {children}
        </TypographyProvider>
      </TextProvider>
    </Box>
  );
}
